import { Dispatch, useEffect, useState } from "react";
import { LOGIN_STATES } from "@constants/Enums";
import { checkRealmExists } from "@services/KeyCloak/KeyCloak";
import { error_message } from "@constants/Errors";
import { setAuthState } from "@services/StateManagement/AuthSlice";
import messages from "@constants/Messages";
import { useDispatch } from "react-redux";
import { UnknownAction } from "@reduxjs/toolkit";
import { KEYCLOAK_EVENTS } from "@constants/Enums";

export const UseAuthMainInitializer = () => {
  const dispatch: Dispatch<UnknownAction> = useDispatch();

  const realmString = "realm";
  const adminRealm: string = import.meta.env.VITE_ADMIN_REALM;
  const appName: string = import.meta.env.VITE_APP_NAME;
  const adminHomePage: string = import.meta.env.VITE_APP_ADMIN_HOME_PAGE;
  const redirectRealm: boolean = import.meta.env.VITE_REALM_REDIRECT === "true";

  document.title = appName;

  const [realm, setRealm] = useState<string | null>(
    localStorage.getItem(realmString),
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [loginState, setLoginState] = useState<string | null>(
    localStorage.getItem(LOGIN_STATES.CURRENT_STATE) === null
      ? "start"
      : localStorage.getItem(LOGIN_STATES.CURRENT_STATE),
  );

  useEffect(() => {
    const previousState: string | null = localStorage.getItem(
      LOGIN_STATES.PREVIOUS_STATE,
    );
    const logInState: string | null = localStorage.getItem(
      LOGIN_STATES.CURRENT_STATE,
    );

    if (previousState == LOGIN_STATES.LOGGING_OUT) {
      localStorage.setItem(LOGIN_STATES.PREVIOUS_STATE, LOGIN_STATES.START);
    }

    if (
      previousState == LOGIN_STATES.START &&
      logInState != LOGIN_STATES.LOGGED_IN
    ) {
      localStorage.setItem(LOGIN_STATES.CURRENT_STATE, LOGIN_STATES.START);
    }
  }, []);

  useEffect(() => {
    const storedRealm: string | null = localStorage.getItem(realmString);
    if (storedRealm != adminRealm) {
      setRealm(storedRealm);
    } else if (
      loginState != LOGIN_STATES.LOGGING_IN &&
      loginState != LOGIN_STATES.LOGGED_IN
    ) {
      setRealm("");
    }
  }, []);

  const handleRealmSelection: (selectedRealm: any) => Promise<void> = async (
    selectedRealm,
  ) => {
    if (selectedRealm != "" && selectedRealm != null) {
      const realmExists: boolean = await checkRealmExists(selectedRealm);

      if (selectedRealm === adminRealm && redirectRealm) {
        window.location.href = adminHomePage;
        return;
      }

      if (realmExists) {
        localStorage.setItem(LOGIN_STATES.PREVIOUS_STATE, loginState);
        localStorage.setItem(
          LOGIN_STATES.CURRENT_STATE,
          LOGIN_STATES.LOGGING_IN,
        );
        setLoginState(localStorage.getItem(LOGIN_STATES.CURRENT_STATE));
        localStorage.setItem(realmString, selectedRealm);
        setRealm(selectedRealm);
        setLoading(true);
      } else {
        setError(error_message.tenant.realm_not_found(selectedRealm));
        return;
      }
    } else {
      setError(error_message.generic.enter_tenant);
      return;
    }
  };

  const handleSelectAdminRealm = () => {
    const previousState: string | null = localStorage.getItem(
      LOGIN_STATES.CURRENT_STATE,
    );
    localStorage.setItem(LOGIN_STATES.PREVIOUS_STATE, loginState);
    localStorage.setItem(LOGIN_STATES.CURRENT_STATE, LOGIN_STATES.LOGGING_IN);
    setLoginState(localStorage.getItem(LOGIN_STATES.CURRENT_STATE));
    localStorage.setItem(realmString, adminRealm);
    setRealm(adminRealm);
    setLoading(true);
  };

  const handleKeycloakEvent = (event: string) => {
    switch (event) {
      case KEYCLOAK_EVENTS.AUTH_SUCCESS:
        dispatch(setAuthState(true));
        localStorage.setItem(
          LOGIN_STATES.PREVIOUS_STATE,
          LOGIN_STATES.LOGGING_IN,
        );
        localStorage.setItem(
          LOGIN_STATES.CURRENT_STATE,
          LOGIN_STATES.LOGGED_IN,
        );
        setLoading(false);
        break;

      case KEYCLOAK_EVENTS.AUTH_LOGOUT:
        dispatch(setAuthState(false));
        localStorage.setItem(
          LOGIN_STATES.PREVIOUS_STATE,
          LOGIN_STATES.LOGGED_IN,
        );
        localStorage.setItem(
          LOGIN_STATES.CURRENT_STATE,
          LOGIN_STATES.LOGGING_OUT,
        );
        setLoginState(LOGIN_STATES.LOGGING_OUT);
        break;

      case KEYCLOAK_EVENTS.AUTH_REFRESH_SUCCESS:
        console.info(messages.keycloak.token_authed);
        break;

      case KEYCLOAK_EVENTS.ON_TOKEN_EXPIRED:
        console.warn(error_message.keycloak.token_expired);
        break;

      default:
        break;
    }
  };

  return {
    adminRealm,
    appName,
    adminHomePage,
    realm,
    loading,
    error,
    loginState,
    handleRealmSelection,
    handleSelectAdminRealm,
    handleKeycloakEvent,
  };
};
